<template>
  <div class="container">
    <div class="box-title">
      <h4>
        <i class="fal fa-calendar-alt"></i>
        {{ $t("fleetRounds.page_title") }}
        <span class="btn-add">
          <vs-button circle icon flat :disabled="!selectedRouteId" @click="OpenCreateRound()">
            <i class="fal fa-plus-circle"></i>
          </vs-button>
        </span>
      </h4>
    </div>

    <div class="box">
      <div class="columns">
        <div class="column is-4">
          <div class="control">
            <label class="label">{{ $t("fleetRounds.route") }}</label>
            <model-list-select
                :list="plannerRouteOptions"
                v-model="selectedRouteId"
                :placeholder="$t('fleetRounds.select_route')"
                option-text="display_name"
                option-value="id"
                @input="OnRouteChanged"
            >
            </model-list-select>
          </div>
        </div>
        <div class="column is-3">
          <div class="control field">
            <label class="label">{{ $t("fleetRounds.date_label") }}</label>
            <date-picker
                format="DD/MM/YYYY"
                value-type="YYYY-MM-DD"
                type="date"
                v-model="serviceDate"
                :clearable="false"
                @change="OnDateChanged"
            ></date-picker>
          </div>
        </div>
      </div>
    </div>

    <div class="columns">
      <!-- Rounds of the selected route + date -->
      <div class="column is-8">
        <div class="center">
          <vs-table>
            <template #thead>
              <vs-tr>
                <vs-th></vs-th>
                <vs-th>{{ $t("fleetRounds.round") }}</vs-th>
                <vs-th>{{ $t("fleetRounds.trip_code") }}</vs-th>
                <vs-th>{{ $t("fleetRounds.plate_number") }}</vs-th>
                <vs-th>{{ $t("fleetRounds.driver") }}</vs-th>
                <vs-th>{{ $t("fleetRounds.departure_time") }}</vs-th>
                <vs-th>{{ $t("fleetRounds.bills_pieces") }}</vs-th>
                <vs-th>{{ $t("fleetRounds.status") }}</vs-th>
              </vs-tr>
            </template>
            <template #tbody>
              <vs-tr :key="index" :data="round" v-for="(round, index) in rounds">
                <vs-td style="width: 60px">
                  <vs-button circle icon flat @click="GoToTripDetail(round.id)">
                    <i class="fal fa-eye"></i>
                  </vs-button>
                </vs-td>
                <vs-td :style="{textAlign: 'center', fontWeight: 'bold'}">{{ round.round_number }}</vs-td>
                <vs-td :style="{color: 'crimson'}">
                  {{ round.trip_code }}
                  <span v-if="round.direction"
                        :style="{color: FleetDirectionColor(round.direction), fontSize: '11px', fontWeight: 700}">
                    [{{ FleetDirectionLabel(round.direction) }}]
                  </span>
                </vs-td>
                <vs-td>
                  <span v-if="round.truck_plate">{{ round.truck_plate }}</span>
                  <span v-else class="missing-chip" :title="$t('fleetRounds.add_truck_before_start')">{{ $t("fleetRounds.no_truck_yet") }}</span>
                </vs-td>
                <vs-td>
                  <span v-if="round.drivers && round.drivers.length">
                    {{ round.drivers.map((d) => $t('fleetRounds.driver_with_card_id', {name: d.driver_name || DriverName(d.driver_id), id: d.driver_card_id || DriverCardId(d.driver_id)})).join(', ') }}
                  </span>
                  <span v-else class="missing-chip">{{ $t("fleetRounds.no_driver_yet") }}</span>
                </vs-td>
                <vs-td>{{ FleetFormatTime(round.planned_departure_at) }}</vs-td>
                <vs-td>
                  <span>
                    {{ FleetTripTotals(round).bills || 0 }} / {{ FleetTripTotals(round).pieces || 0 }}
                  </span>
                  <span v-if="round.totals && round.totals.off_route && round.totals.off_route.pieces"
                        :style="{color: FleetMovementColor('offRoute'), fontSize: '11px', fontWeight: 700}"
                        :title="$t('fleetRounds.off_route_title')">
                    {{ FleetOffRouteText(round.totals.off_route.pieces, round.totals.off_route.on_truck_pieces) }}
                  </span>
                </vs-td>
                <vs-td>
                  <span :style="{color: FleetTripStatusColor(round.status), fontWeight: 'bold'}">
                    {{ FleetTripStatusLabel(round.status) }}
                  </span>
                </vs-td>
              </vs-tr>
            </template>
            <template #footer>
              <Loading v-if="roundsLoading"/>
              <p v-if="!roundsLoading && !rounds.length && selectedRouteId"
                 :style="{textAlign: 'center', color: 'grey', padding: '15px'}">
                {{ $t("fleetRounds.no_rounds_for_route_date") }}
              </p>
              <p v-if="!selectedRouteId" :style="{textAlign: 'center', color: 'grey', padding: '15px'}">
                {{ $t("fleetRounds.select_route_first") }}
              </p>
            </template>
          </vs-table>
        </div>
      </div>

      <!-- Volume + truck-size recommendation panel (US7 feed) -->
      <div class="column is-4">
        <div class="box">
          <label class="label">
            <i class="fal fa-weight-hanging"></i>
            {{ $t("fleetRounds.pending_volume_title") }}
          </label>
          <!-- This panel is a LIVE snapshot, NOT the selected service date: the
               pending set of a past day cannot be reconstructed (one row per
               parcel, overwritten in place). Say so instead of letting the date
               picker above imply otherwise. -->
          <div class="volume-asof">
            <span class="live-chip"><i class="fal fa-signal-stream"></i> {{ $t("fleetRounds.live_data") }}</span>
            <span v-if="volumeAsOf" class="asof-date">{{ $t("fleetRounds.as_of_date", { date: FleetFormatDate(volumeAsOf) }) }}</span>
            <span v-if="volumeMeasuredAt" class="asof-date">{{ $t("fleetRounds.updated_at", { time: FleetFormatTime(volumeMeasuredAt) }) }}</span>
            <span v-if="volumeRefreshing" class="asof-date">{{ $t("fleetRounds.refreshing") }}</span>
          </div>
          <p v-if="PlannerDateIsPast" class="volume-past-note">
            {{ $t("fleetRounds.volume_is_live_note") }}
          </p>
          <Loading v-if="volumeLoading"/>
          <div v-if="!volumeLoading && volumePanel">
            <table class="volume-table">
              <thead>
              <tr>
                <th>{{ $t("fleetRounds.col_stop") }}</th>
                <th>{{ $t("fleetRounds.col_bills") }}</th>
                <th>{{ $t("fleetRounds.col_pieces") }}</th>
                <th>kg</th>
                <th>m³</th>
              </tr>
              </thead>
              <tbody>
              <template v-for="(stop, index) in volumePanel.stops">
                <tr :key="'pending-' + index">
                  <td>{{ stop.stop_name || (FleetStopTypeLabel(stop.stop_type) + ' #' + stop.stop_id) }}</td>
                  <td>{{ stop.pending ? stop.pending.bills : 0 }}</td>
                  <td>{{ stop.pending ? stop.pending.pieces : 0 }}</td>
                  <td>{{ FleetFormatNumber(stop.pending ? stop.pending.weight_kg : 0, 1) }}</td>
                  <td>{{ stop.pending ? stop.pending.cbm : 0 }}</td>
                </tr>
                <tr :key="'off-route-' + index" v-if="FleetHasOffRouteVolume(stop.pending_off_route)"
                    class="off-route-row">
                  <td>{{ $t("fleetRounds.off_route_sub") }}</td>
                  <td>{{ stop.pending_off_route.bills }}</td>
                  <td>{{ stop.pending_off_route.pieces }}</td>
                  <td>{{ FleetFormatNumber(stop.pending_off_route.weight_kg, 1) }}</td>
                  <td>{{ stop.pending_off_route.cbm }}</td>
                </tr>
              </template>
              </tbody>
              <tfoot v-if="volumePanel.totals">
              <tr>
                <td :style="{fontWeight: 'bold'}">{{ $t("fleetRounds.total") }}</td>
                <td :style="{fontWeight: 'bold'}">{{ volumePanel.totals.bills }}</td>
                <td :style="{fontWeight: 'bold'}">{{ volumePanel.totals.pieces }}</td>
                <td :style="{fontWeight: 'bold'}">{{ FleetFormatNumber(volumePanel.totals.weight_kg, 1) }}</td>
                <td :style="{fontWeight: 'bold'}">{{ volumePanel.totals.cbm }}</td>
              </tr>
              <tr v-if="FleetHasOffRouteVolume(volumePanel.totals_off_route)" class="off-route-row">
                <td :style="{fontWeight: 'bold'}">{{ $t("fleetRounds.off_route") }}</td>
                <td :style="{fontWeight: 'bold'}">{{ volumePanel.totals_off_route.bills }}</td>
                <td :style="{fontWeight: 'bold'}">{{ volumePanel.totals_off_route.pieces }}</td>
                <td :style="{fontWeight: 'bold'}">{{ FleetFormatNumber(volumePanel.totals_off_route.weight_kg, 1) }}</td>
                <td :style="{fontWeight: 'bold'}">{{ volumePanel.totals_off_route.cbm }}</td>
              </tr>
              </tfoot>
            </table>
            <div v-if="volumePanel.recommendation" :style="{marginTop: '10px'}">
              <span class="recommend-chip" v-if="volumePanel.recommendation.size_class">
                {{ $t("fleetRounds.recommended", { size: FleetSizeClassLabel(volumePanel.recommendation.size_class) }) }}
                <span v-if="volumePanel.recommendation.utilization_pct !== null">
                  ({{ volumePanel.recommendation.utilization_pct }}%)
                </span>
              </span>
              <span class="recommend-chip none" v-else>{{ $t("fleetRounds.no_fitting_truck") }}</span>
            </div>
          </div>
          <p v-if="!volumeLoading && !volumePanel" :style="{color: 'grey', fontSize: '13px'}">
            {{ $t("fleetRounds.select_route_for_volume") }}
          </p>
        </div>
      </div>
    </div>

    <!-- Create round dialog -->
    <ModalAdd>
      <template v-slot="{ close }">
        <div>
          <h4 class="not-margin"
              :style="{ fontWeight: 'bold', color: '#195BFF', fontSize: '18px', textAlign: 'left' }">
            <i class="fal fa-plus-circle" :style="{ width: '25px', fontSize: '18px' }"></i>
            {{ $t("fleetRounds.create_round_title", { route: SelectedRouteName(), date: FleetFormatDate(roundValue.service_date || serviceDate) }) }}
          </h4>

          <div class="field" :style="{marginTop: '20px'}">
            <div class="control">
              <label class="label">
                {{ $t("fleetRounds.service_date") }}
                <span class="text-danger">{{ server_errors.service_date }}</span>
              </label>
              <date-picker
                  format="DD/MM/YYYY"
                  value-type="YYYY-MM-DD"
                  type="date"
                  v-model="roundValue.service_date"
                  :clearable="false"
                  @change="OnModalDateChanged"
              ></date-picker>
              <p v-if="ModalDateIsPast()" :style="{color: '#e8a10f', fontSize: '12px', marginTop: '4px'}">
                <i class="fal fa-exclamation-triangle"></i>
                {{ $t("fleetRounds.past_date_warning") }}
              </p>
            </div>
          </div>
          <div class="field">
            <div class="control">
              <label class="label">
                {{ $t("fleetRounds.round_number") }}
                <span class="text-danger">{{ server_errors.round_number }}</span>
              </label>
              <input class="input" type="number" min="1" max="20" v-model="roundValue.round_number">
            </div>
          </div>
          <div class="field">
            <div class="control">
              <label class="label">
                {{ $t("fleetRounds.select_truck") }} <small class="optional-note">{{ $t("fleetRounds.truck_optional_note") }}</small>
                <span class="text-danger">{{ server_errors.truck_id }}</span>
              </label>
              <model-list-select
                  :list="trucks"
                  v-model="roundValue.truck_id"
                  :placeholder="$t('fleetRounds.select_truck_placeholder')"
                  option-text="display_name"
                  option-value="id"
              >
              </model-list-select>

              <!-- Per-round capacity utilization: this truck vs the route's
                   currently-pending volume. >100% ⇒ one truck won't fit,
                   plan another round. -->
              <div v-if="roundCapacityHint" class="capacity-hint"
                   :class="roundCapacityHint.over ? 'over' : 'ok'">
                <div class="cap-bar-wrap">
                  <div class="cap-bar" :style="{width: Math.min(roundCapacityHint.limitingPct, 100) + '%'}"></div>
                </div>
                <div class="cap-text">
                  <b>{{ $t("fleetRounds.load_pct", { pct: roundCapacityHint.limitingPct }) }}</b>
                  {{ $t("fleetRounds.of_this_truck") }}
                  <span class="cap-detail">
                    {{ $t("fleetRounds.load_detail", { kg: roundCapacityHint.kgPct !== null ? roundCapacityHint.kgPct + '%' : '—', cbm: roundCapacityHint.cbmPct !== null ? roundCapacityHint.cbmPct + '%' : '—' }) }}
                  </span>
                </div>
                <div v-if="roundCapacityHint.over" class="cap-warn">
                  <i class="fal fa-exclamation-triangle"></i>
                  {{ $t("fleetRounds.over_capacity_warning", { n: roundCapacityHint.roundsNeeded }) }}
                </div>
              </div>
              <p v-else-if="roundValue.truck_id && !volumePanel" class="cap-note">
                {{ $t("fleetRounds.select_date_route_for_load") }}
              </p>
            </div>
          </div>
          <div class="field">
            <div class="control">
              <label class="label">
                {{ $t("fleetRounds.drivers_label") }}
                <small class="optional-note">{{ $t("fleetRounds.drivers_optional_note") }}</small>
                <span class="text-danger">{{ server_errors.driver_ids }}</span>
              </label>
              <multiselect
                  v-model="selectedDrivers"
                  :options="drivers"
                  :multiple="true"
                  :close-on-select="false"
                  label="pickerLabel"
                  track-by="id"
                  :placeholder="$t('fleetRounds.driver_picker_placeholder')"
                  :loading="driversLoading"
              >
              </multiselect>
            </div>
          </div>
          <div class="field">
            <div class="control">
              <label class="label">
                {{ $t("fleetRounds.planned_departure") }}
                <span class="text-danger">{{ server_errors.planned_departure_at }}</span>
              </label>
              <input class="input" type="time" v-model="roundValue.planned_departure_time">
            </div>
          </div>

          <div class="field" v-if="stopTimetable.length">
            <label class="label">{{ $t("fleetRounds.stop_timetable_optional") }}</label>
            <div class="timetable-row" v-for="(row, index) in stopTimetable" :key="index">
              <span class="stop-seq">{{ row.sequence }}</span>
              <span class="stop-name">{{ row.name }}</span>
              <input class="input" type="time" v-model="row.planned_arrival_time" :style="{width: '110px'}">
            </div>
          </div>

          <div class="field">
            <div class="control">
              <label class="label">{{ $t("fleetRounds.remark") }}</label>
              <input class="input" :placeholder="$t('fleetRounds.remark_placeholder')" v-model="roundValue.remark">
            </div>
          </div>

          <div v-if="conflicts.length" class="conflict-box">
            <p :style="{fontWeight: 'bold', color: '#e8a10f'}">
              <i class="fal fa-exclamation-triangle"></i> {{ $t("fleetRounds.warnings_non_blocking") }}
            </p>
            <p v-for="(conflict, index) in conflicts" :key="index" :style="{fontSize: '13px'}">
              - {{ ConflictLabel(conflict) }}
            </p>
          </div>

          <div ref="target" id="target" class="center">
            <div class="con-btns">
              <vs-button ref="button" flat class="form-btn-loading" :loading="btnLoading"
                         @click="SubmitCreateRound(close)">
                {{ $t("fleetRounds.create_round") }}
              </vs-button>
            </div>
          </div>
        </div>
      </template>
    </ModalAdd>
  </div>
</template>

<script>
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import {ModelListSelect} from 'vue-search-select';
import Loading from '@views/Loading';
import MixinFleetRounds from '@/Mixins/Fleet/Rounds';
import MixinFleetRoutes from '@/Mixins/Fleet/Routes';
import {conflictText, FLEET_BASE} from '@/Mixins/Fleet/FleetHelpers';

export default {
  components: {
    DatePicker,
    ModelListSelect,
    Loading,
  },
  mixins: [MixinFleetRounds, MixinFleetRoutes],
  data: () => ({
    selectedDrivers: [],
  }),
  computed: {
    // The PLANNER filter date (distinct from ModalDateIsPast, which reads the
    // create-round dialog's own date). Used only to warn that the live volume
    // panel is not — and cannot be — the volume of a past day.
    PlannerDateIsPast() {
      return !!this.serviceDate
          && this.serviceDate < moment(new Date()).format('YYYY-MM-DD');
    },
    plannerRouteOptions() {
      return this.plannerRoutes.map((route) => ({
        ...route,
        display_name: `${route.code ? route.code + ' — ' : ''}${route.name}${route.is_active ? '' : ' ' + this.$t('fleetRounds.draft_suffix')}`,
      }));
    },
    // Per-round load: the selected truck's capacity vs the route's currently
    // pending outbound volume (volumePanel.totals). Over 100% ⇒ one truck won't
    // fit, so plan roundsNeeded rounds. Pending shrinks as earlier rounds get
    // scanned, so the hint reflects what is LEFT to load.
    roundCapacityHint() {
      if (!this.roundValue.truck_id || !this.volumePanel || !this.volumePanel.totals) return null;
      const truck = this.trucks.find((t) => Number(t.id) === Number(this.roundValue.truck_id));
      if (!truck) return null;

      const capKg = Number(truck.capacity_kg) || 0;
      const capCbm = Number(truck.capacity_cbm) || 0;
      const pendKg = Number(this.volumePanel.totals.weight_kg) || 0;
      const pendCbm = Number(this.volumePanel.totals.cbm) || 0;

      const kgPct = capKg > 0 ? Math.round((pendKg / capKg) * 100) : null;
      const cbmPct = capCbm > 0 ? Math.round((pendCbm / capCbm) * 100) : null;
      const pcts = [kgPct, cbmPct].filter((v) => v !== null);
      if (!pcts.length) return null;

      const limitingPct = Math.max(...pcts);
      return {
        kgPct,
        cbmPct,
        limitingPct,
        roundsNeeded: Math.max(1, Math.ceil(limitingPct / 100)),
        over: limitingPct > 100,
        capKg,
        capCbm,
        pendKg,
        pendCbm,
      };
    },
  },
  methods: {
    ConflictLabel(conflict) {
      return conflictText(conflict);
    },
    SelectedRouteName() {
      const route = this.plannerRoutes.find((r) => Number(r.id) === Number(this.selectedRouteId));
      return route ? route.name : '...';
    },
    // Filters live in the URL query so trip detail -> back keeps the view.
    SyncQuery() {
      const query = {};
      if (this.selectedRouteId) query.route_id = String(this.selectedRouteId);
      if (this.serviceDate) query.date = this.serviceDate;
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({query}).catch(() => {});
      }
    },
    OnRouteChanged() {
      this.SyncQuery();
      this.FetchRounds();
      this.FetchVolumePanel();
    },
    OnDateChanged() {
      this.SyncQuery();
      this.FetchRounds();
      this.FetchVolumePanel();
    },
    OpenCreateRound() {
      const route = this.plannerRoutes.find((r) => Number(r.id) === Number(this.selectedRouteId));
      if (route && !route.is_active) {
        this.$notification.warning({message: this.$t('fleetRounds.route_is_draft_warning')});
      }
      this.server_errors = {};
      this.conflicts = [];
      this.selectedDrivers = [];
      this.roundValue = {
        service_date: this.serviceDate,
        round_number: this.NextRoundNumber(),
        truck_id: '',
        driver_ids: [],
        planned_departure_time: '08:00',
        remark: '',
      };
      // Timetable rows come from the route detail (origin + ordered stops).
      this.FetchRouteDetail(this.selectedRouteId).then((detail) => {
        this.LoadStopTimetable(detail);
      }).catch(() => {
        this.stopTimetable = [];
      });
      this.$store.commit('modalAdd_State', true);
    },
    NextRoundNumber() {
      const numbers = this.rounds.map((round) => Number(round.round_number) || 0);
      return numbers.length ? Math.max(...numbers) + 1 : 1;
    },
    ModalDateIsPast() {
      return !!this.roundValue.service_date
          && this.roundValue.service_date < moment(new Date()).format('YYYY-MM-DD');
    },
    // Round numbers restart every service day, so re-suggest the number
    // whenever the dialog date moves off the planner date.
    OnModalDateChanged() {
      if (!this.selectedRouteId || !this.roundValue.service_date) return;
      if (this.roundValue.service_date === this.serviceDate) {
        this.roundValue.round_number = this.NextRoundNumber();
        return;
      }
      this.$http.get(`${FLEET_BASE}/trips`, {
        params: {
          date: this.roundValue.service_date,
          route_id: this.selectedRouteId,
          per_page: 50,
        },
      }).then((res) => {
        const numbers = (res.data.data || []).map((round) => Number(round.round_number) || 0);
        this.roundValue.round_number = numbers.length ? Math.max(...numbers) + 1 : 1;
      }).catch(() => {
      });
    },
    SubmitCreateRound(close) {
      // Truck + crew are optional (a provincial plate is often known only at
      // 16–17h): the round is created now, the truck added before it starts.
      // With a truck but no crew, the truck's default crew fills in server-side.
      this.roundValue.driver_ids = this.selectedDrivers.map((driver) => driver.id);
      this.CreateRound().then(() => {
        close();
        // Jump the planner to the day the round was created on so the new
        // round is visible immediately (and survives refresh via the URL).
        if (this.roundValue.service_date && this.roundValue.service_date !== this.serviceDate) {
          this.serviceDate = this.roundValue.service_date;
          this.SyncQuery();
          this.FetchVolumePanel();
        }
        this.FetchRounds();
      }).catch(() => {
      });
    },
    GoToTripDetail(tripId) {
      this.$router.push({name: 'fleet.trips.detail', params: {id: tripId}});
    },
  },
  created() {
    // Restore route/date carried in the URL (back from trip detail).
    const query = this.$route.query || {};
    if (query.date) this.serviceDate = query.date;
    if (query.route_id) this.selectedRouteId = Number(query.route_id) || '';

    this.FetchPlannerRoutes();
    this.FetchActiveTrucks();
    this.FetchDrivers();
    if (this.selectedRouteId) {
      this.FetchRounds();
      this.FetchVolumePanel();
    }
  },
};
</script>

<style lang="scss" scoped>
.volume-asof {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.live-chip {
  display: inline-block;
  background: #2dcc70;
  color: white;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: bold;
  font-family: $font;
}

.asof-date {
  color: grey;
  font-size: 12px;
  margin-left: 8px;
  font-family: $font;
}

.volume-past-note {
  color: #e8a10f;
  font-size: 12px;
  margin-bottom: 8px;
  font-family: $font;
}

.volume-table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;

  th, td {
    padding: 4px 6px;
    border-bottom: 1px solid #f0f0f0;
    text-align: left;
  }

  th {
    color: #666;
  }

  .off-route-row td {
    color: #e8a10f;
  }
}

.recommend-chip {
  display: inline-block;
  background: #2dcc70;
  color: white;
  border-radius: 12px;
  padding: 3px 12px;
  font-size: 13px;
  font-weight: bold;

  &.none {
    background: crimson;
  }
}

.capacity-hint {
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 12px;
  border: 1px solid #bfe3bf;
  background: #f1f9f1;

  &.over {
    border-color: #f3c0b8;
    background: #fdf1ee;
  }
}

.cap-bar-wrap {
  height: 8px;
  border-radius: 5px;
  background: #e6e8ec;
  overflow: hidden;
  margin-bottom: 6px;
}

.cap-bar {
  height: 100%;
  background: #2dcc70;
  transition: width 0.2s ease;

  .over & {
    background: #e2513c;
  }
}

.cap-text {
  color: #444;
}

.cap-detail {
  color: #777;
}

.cap-warn {
  margin-top: 5px;
  color: #e2513c;
  font-weight: bold;

  i {
    margin-right: 3px;
  }
}

.cap-note {
  margin-top: 8px;
  color: grey;
  font-size: 12px;
}

.timetable-row {
  display: flex;
  align-items: center;
  padding: 3px 0;
  font-size: 13px;

  .stop-seq {
    width: 24px;
    height: 24px;
    min-width: 24px;
    border-radius: 50%;
    background: #195BFF;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    margin-right: 8px;
  }

  .stop-name {
    flex: 1;
  }
}

.conflict-box {
  background: #fff8e6;
  border: 1px solid #f3d488;
  border-radius: 6px;
  padding: 10px;
  margin: 10px 0;
}
.missing-chip {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  background: #fff4e0;
  color: #b26a00;
  font-size: 12px;
}

.optional-note {
  color: #888;
  font-weight: normal;
}
</style>
